import React, { Component, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Card, CardBody, Col } from 'reactstrap';
import moment from 'moment';
import Board from './Board';
import { getPetSummary } from '../../../redux/actions/petActions';
import {
  getByPhase,
  savePhaseMessage,
  getPtbEvents,
  clearStore,
} from '../../../redux/actions/pTBActions';
import { getStaffDoctorsByFacility } from '../../../redux/actions/staffActions';
import PhaseNotificationModel from './PhaseNotificationModel';
import MessageModal from './../../../shared/components/Modals/MessageModal';
import RefreshBoardButton from './RefreshBoardButton';
import HeadingText from '../../../shared/components/form/HeadingText';
import StandardTableToolbar from '../../../shared/components/table-toolbar/StandardTableToolbar';
import { TRACKING_BOARD_DATE_FORMAT } from '../../../constants/constants';
import { handleRequest } from '../../../redux/services/APIService';
import { getAppointmentLabels } from '../../App/customize/hooks/useAppointmentLabels';
import { getSessionFacilityServicesSeed } from '../../App/customize/hooks/useGetFacilityServices';
import { formatAppointmentTypeTitle } from '../../App/customize/hooks/useGetAppointmentTypes';
import storageHelper from '../../../libs/storageHelper';
import {
  PTB_DEFAULT_SORT_FIELD,
  PTB_DEFAULT_SORT_ORDER,
  PTB_SORT_OPTIONS,
  PTB_FILTER_SEARCH_THRESHOLD,
  PTB_PHASE_REMEMBER_LABEL,
  PTB_RELOAD_EVENT,
  buildPtbRequestParams,
  getPtbFilterCategories,
  getPhaseChipDefinitions,
  getApplicablePhaseIds,
  loadPtbPhaseDisplay,
  savePtbPhaseDisplay,
} from './constants';

function getPhasesButtonLabel(hiddenCount) {
  if (!hiddenCount) {
    return 'Phases';
  }
  if (hiddenCount === 1) {
    return 'Phases · 1 hidden';
  }
  return `Phases · ${hiddenCount} hidden`;
}

function PtbPhasePanel({
  isOpen,
  anchorRef,
  onClose,
  helperText,
  hiddenPhaseIds,
  phaseRemember,
  isBoardingOnly,
  onTogglePhase,
  onRestoreAllPhases,
  onPhaseRememberChange,
}) {
  const panelRef = useRef(null);
  const phaseChips = getPhaseChipDefinitions(isBoardingOnly);
  const hiddenSet = new Set((hiddenPhaseIds || []).map((id) => +id));
  const allVisible = hiddenSet.size === 0;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      onClose();
    };

    const handleMouseDown = (event) => {
      const anchor = anchorRef?.current;
      if (
        panelRef.current?.contains(event.target) ||
        anchor?.contains(event.target)
      ) {
        return;
      }
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [anchorRef, isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className="table-toolbar__panel table-toolbar__panel--right ptb-phase-panel"
      role="dialog"
      aria-label="Phase display"
    >
      <p className="ptb-phase-panel__helper">{helperText}</p>
      <div className="ptb-phase-panel__controls">
        <button
          type="button"
          className={`ptb-phase-panel__restore${
            allVisible ? ' is-disabled' : ''
          }`}
          disabled={allVisible}
          onClick={onRestoreAllPhases}
        >
          Restore all Phases
        </button>
        <label className="ptb-phase-panel__remember">
          <input
            type="checkbox"
            checked={!!phaseRemember}
            onChange={onPhaseRememberChange}
          />
          <span>{PTB_PHASE_REMEMBER_LABEL}</span>
        </label>
      </div>
      <div
        className={
          'ptb-phase-panel__chips' +
          (isBoardingOnly
            ? ' ptb-phase-panel__chips--boarding'
            : ' ptb-phase-panel__chips--vet')
        }
      >
        {phaseChips.map((phase) => {
          const isVisible = !hiddenSet.has(phase.id);
          return (
            <button
              key={phase.id}
              type="button"
              className={`ptb-phase-chip${
                isVisible ? ' ptb-phase-chip--visible' : ' ptb-phase-chip--hidden'
              }`}
              style={
                isVisible
                  ? {
                      backgroundColor: phase.color,
                      borderColor: phase.color,
                    }
                  : {
                      color: phase.color,
                      borderColor: phase.color,
                    }
              }
              onClick={() => onTogglePhase(phase.id)}
            >
              {phase.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function getInitialPtbBoardContext() {
  const loggedUser = storageHelper.getFromStorage('loggedUser');
  const facilityId = loggedUser?.user?.facility;
  const isBoardingOnly = getSessionFacilityServicesSeed()?.isBoardingOnly === true;

  if (!facilityId) {
    return {
      isBoardingOnly: false,
      hiddenPhaseIds: [],
      phaseRemember: false,
    };
  }

  const phaseDisplay = loadPtbPhaseDisplay(facilityId, isBoardingOnly);

  return {
    isBoardingOnly,
    hiddenPhaseIds: phaseDisplay.hiddenPhaseIds,
    phaseRemember: phaseDisplay.remember,
  };
}

class PetTrackingBoard extends Component {
  constructor(props) {
    super(props);
    this.queryRef = { current: buildPtbRequestParams({}) };
   this.phasesWrapRef = React.createRef();
    this.boardContextFacilityId = null;
    const initialBoardContext = getInitialPtbBoardContext();
    this.state = {
      toggleClass: 'minimize',
      showPetModel: false,
      petSummary: [],
      facility: null,
      events: [],
      phaseMessages: [],
      cardAck: null,
      alertShow: false,
      alertMessage: '',
      search: '',
      sortField: PTB_DEFAULT_SORT_FIELD,
      sortOrder: PTB_DEFAULT_SORT_ORDER,
      filters: {},
      hiddenPhaseIds: initialBoardContext.hiddenPhaseIds,
      phaseRemember: initialBoardContext.phaseRemember,
      showPhasePanel: false,
      isBoardingOnly: initialBoardContext.isBoardingOnly,
      appointmentTypes: [],
      boardViewMode: 'board',
      newlyAddedAppointmentId: null,
    };
  }

componentDidMount() {
  const { user } = this.props;

  this.ensureInitialBoardLoad(user?.profile?.facility);

  window.addEventListener(PTB_RELOAD_EVENT, this.handlePtbReloadEvent);

  this.midnightTimer = setInterval(() => {
    const time = moment().format('hma');
    if (time.toString() === '120am') {
      this.reloadPtbBoard();
    }
  }, 30000);
}

  componentWillUnmount() {
    window.removeEventListener(PTB_RELOAD_EVENT, this.handlePtbReloadEvent);
    if (this.midnightTimer) {
      clearInterval(this.midnightTimer);
    }
  }

  handlePtbReloadEvent = (event) => {
  const rawId = event?.detail?.appointmentId;
  if (rawId != null && rawId !== '') {
    this.setState({ newlyAddedAppointmentId: String(rawId) }, () => {
      this.reloadPtbBoard();
    });
    return;
  }
  this.reloadPtbBoard();
};

  syncQueryRef = (overrides = {}) => {
    const state = { ...this.state, ...overrides };
    this.queryRef.current = buildPtbRequestParams({
      search: state.search,
      sortField: state.sortField,
      sortOrder: state.sortOrder,
      filters: state.filters,
    });
  };

  reloadPtbBoard = (facilityOverride) => {
    const facility =
      facilityOverride ||
      this.state.facility ||
      this.props.user?.profile?.facility;
    if (!facility?._id) {
      return;
    }

    this.props.getPtbEvents(
      facility._id,
      facility.utcOffset,
      this.queryRef.current,
    );
  };

ensureInitialBoardLoad = (facility) => {
  if (!facility?._id || facility.utcOffset == null) {
    return;
  }

  const facilityId = facility._id;
  if (facilityId === this.boardContextFacilityId) {
    return;
  }

  this.boardContextFacilityId = facilityId;
  this.setState({ facility });
  this.syncQueryRef();
  this.reloadPtbBoard(facility);
  this.loadBoardContext(facility);
};

  loadBoardContext = async (facility) => {
    const facilityId = facility?._id || facility;
    if (!facilityId) {
      return;
    }
    let isBoardingOnly = false;

    try {
      const response = await handleRequest('get', `facility/${facilityId}`, true);
      const services = response.data?.services || { veterinaryOffered: true };
      isBoardingOnly =
        services.boardingOffered === true && services.veterinaryOffered === false;
    } catch (error) {
      console.error(error);
    }

    const phaseDisplay = loadPtbPhaseDisplay(facilityId, isBoardingOnly);

    let appointmentTypes = [];
    try {
      const typesResponse = await handleRequest(
        'get',
        `facility/${facilityId}/appointment-types`,
        true,
        null,
        { used: 1 },
      );
      appointmentTypes = (typesResponse.data || []).map((type) => ({
        ...type,
        title: formatAppointmentTypeTitle(type.title),
      }));
    } catch (error) {
      console.error(error);
    }

    if (!isBoardingOnly) {
      this.props.getStaffDoctorsByFacility();
    }

  this.setState({
    isBoardingOnly,
    hiddenPhaseIds: phaseDisplay.hiddenPhaseIds,
    phaseRemember: phaseDisplay.remember,
    appointmentTypes,
  });
};

UNSAFE_componentWillReceiveProps(nextProps) {
  const { user, ptb, pets } = nextProps;

  if (nextProps) {
    if (
      ptb.phaseMessage &&
      ptb.phaseMessage.phaseMessages &&
      !ptb.ptbMessageSave
    ) {
      this.setState({
        phaseMessages: ptb.phaseMessage.phaseMessages,
        ptbMessages: ptb.phaseMessage.ptbMessages,
      });
    }
    if (ptb.ptbMessageSave) {
      const { appointmentId } = this.state;

      this.setState(
        {
          showPetModel: false,
          cardAck: {
            cardId: appointmentId,
            message: 'Update sent',
          },
        },
        () => {
          this.reloadPtbBoard();
          this.props.clearStore();
        },
      );
    }

    if (user.profile && user.profile.facility) {
      const facility = user.profile.facility;
      this.ensureInitialBoardLoad(facility);
      if (!ptb.loaded || ptb.cardDrag) {
        if (ptb.cardDrag?.activeStatus) {
          this.setState({
            showPetModel: false,
            alertShow: true,
            alertMessage: this.alertMessage(ptb.cardDrag),
          });
        }

        this.reloadPtbBoard(facility);
      }
    } else if (ptb.cardDrag) {
      if (ptb.cardDrag?.activeStatus) {
        this.setState({
          showPetModel: false,
          alertShow: true,
          alertMessage: this.alertMessage(ptb.cardDrag),
        });
      }

      this.reloadPtbBoard();
    }

    this.setState({ events: ptb.ptbEvents });

      if (pets && pets.petSummary) {
        this.setState({ petSummary: pets.petSummary });
      }
    }
  }

  alertMessage(params) {
    const { parentStatus, petName, parentName, ongoingAppointmentsStatus } =
      params;
    if (!ongoingAppointmentsStatus) {
      return `${petName} has an ongoing procedure. Please complete the ongoing procedure before starting a new procedure.`;
    } else if (!parentStatus) {
      return `${parentName} is inactive. Please activate parent to update the procedure.`;
    } else {
      return `${petName} is inactive. Please activate pet to update the procedure.`;
    }
  }

  maximize = () => {
    this.setState({ toggleClass: 'maximize' });
  };

  minimize = () => {
    this.setState({ toggleClass: 'minimize' });
  };

  toggleBoardViewMode = () => {
    this.setState((prev) => ({
      boardViewMode: prev.boardViewMode === 'grid' ? 'board' : 'grid',
    }));
  };

  openPetModel = (cardId, metadata, laneId, drag) => {
    if (drag) {
      drag = {
        cardId,
        laneId,
        type: metadata.type,
      };
    }

    this.setState(
      { phaseId: laneId, appointmentId: cardId, showPetModel: true, drag },
      () => {
        this.props.getPetSummary(metadata.petId);
        this.props.getByPhase({ phaseId: laneId, appointmentId: cardId });
      },
    );
  };

  closePTBModal = () => {
    this.setState({ showPetModel: false });
  };

  clearCardAck = () => {
  this.setState({ cardAck: null });
  };

  clearNewlyAddedFeedback = () => {
    this.setState({ newlyAddedAppointmentId: null });
  };

handleMessageSubmit = (messages, drag, gallery = []) => {
    const { appointmentId, phaseId } = this.state;
    this.setState({ showPetModel: true });
    this.props.savePhaseMessage({
      messages,
      phaseId,
      appointmentId,
      drag,
      ...(gallery.length ? { gallery } : {}),
    });
  };

  handleSearchChange = (search) => {
    this.setState({ search }, () => {
      this.syncQueryRef();
      this.reloadPtbBoard();
    });
  };

  handleSortChange = ({ field, order }) => {
    this.setState(
      {
        sortField: field ?? this.state.sortField,
        sortOrder: order ?? this.state.sortOrder,
      },
      () => {
        this.syncQueryRef();
        this.reloadPtbBoard();
      },
    );
  };

  handleFilterChange = (key, values) => {
    const filters = { ...this.state.filters, [key]: values };
    this.setState({ filters }, () => {
      this.syncQueryRef();
      this.reloadPtbBoard();
    });
  };

  handleClearAllFilters = () => {
    this.setState({ filters: {} }, () => {
      this.syncQueryRef();
      this.reloadPtbBoard();
    });
  };

  togglePhasePanel = () => {
    this.setState((prev) => ({ showPhasePanel: !prev.showPhasePanel }));
  };

  closePhasePanel = () => {
    this.setState({ showPhasePanel: false });
  };

  persistPhaseDisplay = (hiddenPhaseIds, phaseRemember) => {
    const { facility, isBoardingOnly } = this.state;
    if (!facility?._id) {
      return;
    }

    savePtbPhaseDisplay(
      facility._id,
      { remember: phaseRemember, hiddenPhaseIds },
      isBoardingOnly,
    );
  };

  handleTogglePhase = (phaseId) => {
    const { hiddenPhaseIds, phaseRemember } = this.state;
    const hidden = new Set((hiddenPhaseIds || []).map((id) => +id));
    const id = +phaseId;

    if (hidden.has(id)) {
      hidden.delete(id);
    } else if (getApplicablePhaseIds(this.state.isBoardingOnly).includes(id)) {
      hidden.add(id);
    }

    const nextHiddenPhaseIds = [...hidden];

    this.setState({ hiddenPhaseIds: nextHiddenPhaseIds }, () => {
      if (phaseRemember) {
        this.persistPhaseDisplay(nextHiddenPhaseIds, true);
      }
    });
  };

  handleRestoreAllPhases = () => {
    this.setState({ hiddenPhaseIds: [] }, () => {
      if (this.state.phaseRemember) {
        this.persistPhaseDisplay([], true);
      }
    });
  };

  handlePhaseRememberChange = (event) => {
    const phaseRemember = event.target.checked;
    this.setState({ phaseRemember }, () => {
      this.persistPhaseDisplay(this.state.hiddenPhaseIds, phaseRemember);
    });
  };

  closePopup = () => {
    this.setState({ alertShow: false });
  };

  render() {
    const {
      toggleClass,
      showPetModel,
      drag,
      petSummary,
      phaseMessages,
      ptbMessages,
      facility,
      events,
      cardAck,
      alertShow,
      alertMessage,
      search,
      sortField,
      sortOrder,
      filters,
      hiddenPhaseIds,
      phaseRemember,
      showPhasePanel,
      isBoardingOnly,
      appointmentTypes,
      boardViewMode,
      newlyAddedAppointmentId,
    } = this.state;
    const labels = getAppointmentLabels(isBoardingOnly);
    const doctors = this.props.staff?.staffDoctorsByFacility || [];
    const ptbLoaded = this.props.ptb?.loaded;
    const hiddenPhaseCount = (hiddenPhaseIds || []).length;
    const phasesButtonLabel = getPhasesButtonLabel(hiddenPhaseCount);

    return (
      <div className="patient-tracking">
        <Col md={12} lg={12}>
          <Card>
            <CardBody>
              <div className="ptb-board-header">
                <div className="page-title-with-action ptb-board-header__title-row">
                  <HeadingText text={labels.trackingBoard} />
                  <RefreshBoardButton onRefreshClick={this.reloadPtbBoard} />
                </div>
                <div className="ptb-board-header__subtitle-row">
                  <div className="ptb-board-header__subtitle">
                    Manage and send updates to Pet Parents
                  </div>
                </div>
                <div className="ptb-board-header__date-row">
                  <div className="ptb-board-header__date">
                    {moment().format(TRACKING_BOARD_DATE_FORMAT)}
                  </div>
                </div>
              </div>

              <div className="ptb-board-toolbar">
                <div className="ptb-board-toolbar__controls">
                  <StandardTableToolbar
                    searchValue={search}
                    onSearchChange={this.handleSearchChange}
                    searchAutofocus={true}
                    searchPlaceholder=""
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSortChange={this.handleSortChange}
                    sortOptions={PTB_SORT_OPTIONS}
                    filters={filters}
                    onFilterChange={this.handleFilterChange}
                    onClearAllFilters={this.handleClearAllFilters}
                    filterCategories={getPtbFilterCategories({
                      appointmentTypes,
                      doctors,
                      isBoardingOnly,
                    })}
                    filterSearchThreshold={PTB_FILTER_SEARCH_THRESHOLD}
                  />
                  <div
                    ref={this.phasesWrapRef}
                    className="ptb-board-toolbar__phases-wrap table-toolbar__menu-wrap"
                  >
                    <button
                      type="button"
                      className="table-toolbar__trigger"
                      aria-expanded={showPhasePanel}
                      aria-haspopup="dialog"
                      onClick={this.togglePhasePanel}
                    >
                      {phasesButtonLabel}
                    </button>
                    <PtbPhasePanel
                      isOpen={showPhasePanel}
                      anchorRef={this.phasesWrapRef}
                      onClose={this.closePhasePanel}
                      helperText={labels.ptbPhaseFilterHideHelper}
                      hiddenPhaseIds={hiddenPhaseIds}
                      phaseRemember={phaseRemember}
                      isBoardingOnly={isBoardingOnly}
                      onTogglePhase={this.handleTogglePhase}
                      onRestoreAllPhases={this.handleRestoreAllPhases}
                      onPhaseRememberChange={this.handlePhaseRememberChange}
                    />
                  </div>
                </div>
              </div>

              <div className="icon-maximize" style={{ display: 'none' }}>
                <span
                  onClick={this.maximize}
                  className="lnr lnr-frame-expand"
                />
              </div>

{ptbLoaded ? (
  <Board
    toggleClass={toggleClass}
    minimize={this.minimize}
    openPetModel={this.openPetModel}
    events={events}
    search={search}
    hiddenPhaseIds={hiddenPhaseIds}
    phaseRemember={phaseRemember}
    boardViewMode={boardViewMode}
    onToggleBoardViewMode={this.toggleBoardViewMode}
    onReloadBoard={this.reloadPtbBoard}
    cardAck={cardAck}
    onCardAckDismiss={this.clearCardAck}
    newlyAddedAppointmentId={newlyAddedAppointmentId}
    onNewlyAddedFeedbackComplete={this.clearNewlyAddedFeedback}
  />
) : null}
            </CardBody>
          </Card>

          <PhaseNotificationModel
            show={showPetModel}
            key={56}
            petSummary={petSummary}
            phaseMessages={phaseMessages}
            ptbMessages={ptbMessages}
            facility={facility}
            drag={drag}
            handleMessageSubmit={(message, dragArg, gallery) =>
              this.handleMessageSubmit(message, dragArg, gallery)
            }
            closePTBModal={() => this.closePTBModal()}
          />
        </Col>

        <MessageModal
          show={alertShow}
          type="alert"
          alertType="danger"
          onClose={this.closePopup}
          footer="true"
          message={alertMessage}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  ...state,
});

const mapDispatchToProps = {
  getPetSummary,
  getByPhase,
  getPtbEvents,
  savePhaseMessage,
  clearStore,
  getStaffDoctorsByFacility,
};

export default connect(mapStateToProps, mapDispatchToProps)(PetTrackingBoard);
